import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { UserProfile, CreateAdminUserRequest, UpdateUserRequest } from '../types/user.types';
import { APIResponse } from '../types/survey.types';
import { retryFirebaseCall } from '../utils/retry';

interface UseUsersReturn {
  loading: boolean;
  error: string | null;
  users: UserProfile[];
  createUser: (data: CreateAdminUserRequest) => Promise<string>;
  listUsers: () => Promise<UserProfile[]>;
  updateUser: (data: UpdateUserRequest) => Promise<void>;
  deleteUser: (uid: string) => Promise<void>;
  revokeAllSessions: (uid: string) => Promise<void>;
}

export const useUsers = (): UseUsersReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);

  // Create a new admin user
  const createUser = useCallback(async (data: CreateAdminUserRequest): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const createAdminUserFn = httpsCallable<CreateAdminUserRequest, APIResponse<string>>(
        functions,
        'createAdminUser'
      );

      const result = await retryFirebaseCall(
        () => createAdminUserFn(data),
        'createAdminUser'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to create user');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create user';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // List all users
  const listUsers = useCallback(async (): Promise<UserProfile[]> => {
    setLoading(true);
    setError(null);

    try {
      const listUsersFn = httpsCallable<{ limit?: number }, APIResponse<{ users: UserProfile[] }>>(
        functions,
        'listUsers'
      );

      const result = await retryFirebaseCall(
        () => listUsersFn({ limit: 100 }),
        'listUsers'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to list users');
      }

      const userList = result.data.data.users;
      setUsers(userList);
      return userList;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to list users';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update user
  const updateUser = useCallback(async (data: UpdateUserRequest): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const updateUserFn = httpsCallable<UpdateUserRequest, APIResponse<void>>(
        functions,
        'updateUser'
      );

      const result = await retryFirebaseCall(
        () => updateUserFn(data),
        'updateUser'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to update user');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update user';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete (deactivate) user
  const deleteUser = useCallback(async (uid: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const deleteUserFn = httpsCallable<{ uid: string }, APIResponse<void>>(
        functions,
        'deleteUser'
      );

      const result = await retryFirebaseCall(
        () => deleteUserFn({ uid }),
        'deleteUser'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to delete user');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete user';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Revoke all sessions (logout all devices)
  const revokeAllSessions = useCallback(async (uid: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const revokeAllSessionsFn = httpsCallable<{ uid: string }, APIResponse<void>>(
        functions,
        'revokeAllSessions'
      );

      const result = await retryFirebaseCall(
        () => revokeAllSessionsFn({ uid }),
        'revokeAllSessions'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to revoke sessions');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to revoke sessions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    users,
    createUser,
    listUsers,
    updateUser,
    deleteUser,
    revokeAllSessions,
  };
};
